import { Folder } from "../../redux/interfaces/folder.types.ts";
import { EyeIcon } from "@heroicons/react/24/solid";
import { message, Tooltip } from "antd";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import {
  fetchFolderPdf,
  savePdfUrl,
} from "../../redux/actions/folderActions.tsx";
import { RootState } from "../../redux/store.ts";
import { PDFPreview } from "../base-requirements/requirements/PDFPreview.tsx";
import { folderExceptions } from "../../exceptions/default/folderExceptions.ts";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";

interface PreviewFolderProps {
  folder: Folder;
  isAdminOrOps: boolean;
  subFoldersAreReady: boolean;
  isAssistantUser: boolean | undefined;
  isCjaUser: boolean | undefined;
  isIconViewValidated: boolean | undefined;
}

export const PreviewFolder: React.FC<PreviewFolderProps> = ({
  folder,
  isAdminOrOps,
  subFoldersAreReady,
  isIconViewValidated,
  isCjaUser,
  isAssistantUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [filePreviewOpened, setFilePreviewOpened] = useState(false);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const dispatch = useAppDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const pdfUrl = useAppSelector(
    (state: RootState) => state.folder.pdfUrls[folder.folder_number],
  );

  const tooltipTitle = (() => {
    // CJA user needs complete file delivery
    if (isCjaUser && !folder.is_multireport_delivered) {
      return folderExceptions.completeFileFolder;
    }

    // Non-CJA users need proper permissions and folder readiness
    if (!isCjaUser) {
      if (!isAdminOrOps && !isAssistantUser)
        return folderExceptions.isNotAdminOrOps;
      if (!subFoldersAreReady) return folderExceptions.subfoldersAreNotReady;
      if (!folder.analysis) return folderExceptions.folderAnalysis;
    }

    return "Previsualizar reporte de inquilino";
  })();

  const handlePreview = async () => {
    setFilePreviewOpened(true);
    setLoading(true);

    if (pdfUrl) {
      setFilePreview(pdfUrl);
      setLoading(false);
    } else {
      try {
        const response = await dispatch(
          fetchFolderPdf(folder.folder_number),
        ).unwrap();

        setFilePreview(response);
        dispatch(
          savePdfUrl({ folderNumber: folder.folder_number, url: response }),
        );
      } catch (error: any) {
        console.error("error", error);
        const errorMessage = extractErrorMessage(
          error,
          "No se pudo generar la vista previa.",
        );
        setFileError(errorMessage);
        messageApi.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenInNewTab = () => {
    if (filePreview) {
      window.open(filePreview, "_blank");
    }
  };

  return (
    <div>
      {contextHolder}
      <PDFPreview
        pdfURL={filePreview}
        opened={filePreviewOpened}
        handleOpen={setFilePreviewOpened}
        loading={loading}
        fileError={fileError}
        onOpenInNewTab={handleOpenInNewTab}
      />
      <Tooltip title={tooltipTitle} className="cursor-pointer">
        <EyeIcon
          color="gray"
          className={`h-6 w-6 ${!isIconViewValidated ? "text-gray-300" : "text-gray-600 hover:text-blue-400"}`}
          onClick={!isIconViewValidated ? undefined : handlePreview}
        />
      </Tooltip>
    </div>
  );
};
